import React, { useState, useEffect, useCallback } from "react";
import { services } from "../services";
import DataTable from "react-data-table-component";
import { FaEdit } from "react-icons/fa";
import { QUALIFICATION_NAME } from "@iibf/shared-resources/consts";
import moment from "moment";
import { PartnerUserActionFragment } from "./CandidateStats/PartnerUserActionFragment";
import Select from "react-select";
import { toast } from "react-toastify";
import { bcbfCourseIds, draCourseIds } from "../config";
import { useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { getCookieObject } from "../utils/cookie";

const PartnerCandidatesList = () => {
  const [partnersList, setPartnersList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [candidatesList, setCandidatesList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [partnerSummary, setPartnerSummary] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [paginationParams, setPaginationParams] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalUsersCount, setTotalUsersCount] = useState(0);

  const courseIds = [...draCourseIds, ...bcbfCourseIds];

  const decodedToken = useMemo(() => {
    const { access_token: accessToken } = getCookieObject();
    if (!accessToken) return null;
    try {
      return jwtDecode(accessToken);
    } catch (err) {
      console.error("Invalid token", err);
      return null;
    }
  }, []);

  const candidatePartnerId = decodedToken?.partnerId;

  const fetchPartnersList = async () => {
    try {
      const res = await services.requestSvc.get("/partners");
      setPartnersList(res);
    } catch (error) {
      console.error("Error fetching partners list", error);
    }
  };
  const fetchCandidatesList = useCallback(
    async (
      partnerId = null,
      courseId = null,
      languageId = null,
      batchIds = [],
      pagination = paginationParams,
      sort = []
    ) => {
      try {
        const payload = {
          filter: {
            partnerId: candidatePartnerId ? candidatePartnerId : partnerId,
            courseId,
            languageId,
            batchIds,
          },
          paginationParams: pagination,
          sort,
        };

        const res = await services.requestSvc.post(
          "/partners-candidate-list",
          payload
        );

        setCandidatesList(res.data || []);
        setTotalUsersCount(res.pagination?.totalUsersCount || 0);
      } catch (error) {
        console.error("Error fetching candidates list", error);
      }
    },
    [candidatePartnerId, paginationParams.pageIndex, paginationParams.pageSize]
  );

  const courseIdToName = new Map();
  courses.forEach((c) => {
    if (c?.value && c?.label) {
      courseIdToName.set(String(c.value), c.label);
    }
  });

  useEffect(() => {
    const fetchPartnerSummary = async () => {
      try {
        const body = { courseIds };
        if (candidatePartnerId) body.partnerId = candidatePartnerId;

        const res = await services.requestSvc.post(
          "/partners-candidate-summary-simple",
          body
        );
        setPartnerSummary(res || []);
      } catch (err) {
        console.error("Error fetching partner summary", err);
      }
    };

    fetchPartnerSummary();
  }, [candidatePartnerId]);

  useEffect(() => {
    const effectiveCourseIds = candidatePartnerId
      ? partnerSummary.map((item) => item.courseId)
      : courseIds;
    if (!effectiveCourseIds.length) return;

    services.requestSvc
      .post("/get-iibf-course-batches", {
        course_ids: effectiveCourseIds,
      })
      .then((response) => {
        if (response.success) {
          const courseBatchOptionsInfo = response.data?.map((course) => ({
            value: course.id,
            label: course.full_name,
            batches: course.batches,
          }));
          setCourses(courseBatchOptionsInfo);
        } else {
          toast.error("Failed to fetch courses: " + response.message);
        }
      })
      .catch(() => {
        toast.error("Something went wrong while fetching courses.");
      });
  }, [candidatePartnerId, partnerSummary]);

  useEffect(() => {
    fetchPartnersList();
  }, []);
  useEffect(() => {
    const batchIds = selectedBatches.map((b) => b.value);

    fetchCandidatesList(
      selectedPartner,
      batchIds.length > 0 ? null : selectedCourse?.value,
      selectedLanguage,
      batchIds,
      paginationParams
    );
  }, [
    selectedPartner,
    selectedCourse?.value,
    selectedLanguage,
    selectedBatches.map((b) => b.value).join(","),
    paginationParams.pageIndex,
    paginationParams.pageSize,
  ]);

  const handleCourseChange = (course) => {
    setSelectedCourse(course);
    setSelectedLanguage("");
    setSelectedBatches([]);

    if (course && course.batches) {
      const uniqueLanguages = Array.from(
        new Map(
          course.batches.map((batch) => [
            batch.language.id,
            { value: batch.language.id, label: batch.language.display_name },
          ])
        ).values()
      );
      setLanguages(uniqueLanguages);
    } else {
      setLanguages([]);
    }
  };
  const handleBatchChange = (batch) => {
    setSelectedBatches(batch);
  };
  const batchOptions = useMemo(() => {
    if (selectedCourse && selectedCourse.batches) {
      return selectedCourse.batches.map((b) => ({
        value: b.id,
        label: b.batch_name,
      }));
    }

    return courses.flatMap((c) =>
      c.batches.map((b) => ({
        value: b.id,
        label: b?.batch_name,
      }))
    );
  }, [selectedCourse, courses]);

  const summaryColumns = [
    {
      name: "Partner",
      selector: (row) => row.partnerName,
      sortable: true,
      width: "10rem",
    },
    ...courseIds.map((cid) => ({
      name: (
        <div
          style={{
            whiteSpace: "normal",
            lineHeight: "1.2",
          }}
        >
          {courseIdToName.get(cid) || `Course ${cid}`}
        </div>
      ),
      selector: (row) => row[cid] || 0,
      cell: (row) => <span>{row[cid] || 0} </span>,
      sortable: true,
      width: "10rem",
    })),
    {
      name: "Total",
      selector: (row) => row.total || 0,
      cell: (row) => <span>{row.total || 0} </span>,
      sortable: true,
      width: "10rem",
    },
  ];
  const summaryRows = useMemo(() => {
    const map = new Map();

    partnerSummary.forEach((item) => {
      const pKey = String(item.partnerId);

      if (!map.has(pKey)) {
        const initial = {
          partnerId: pKey,
          partnerName: item.partnerName,
          total: 0,
        };
        courseIds.forEach((cid) => {
          initial[String(cid)] = 0;
        });
        map.set(pKey, initial);
      }

      const row = map.get(pKey);
      const cid = String(item.courseId);
      if (courseIds.map(String).includes(cid)) {
        row[cid] = (row[cid] || 0) + (item.count || 0);
      }
    });

    for (const row of map.values()) {
      row.total = courseIds
        .map(String)
        .reduce((acc, cid) => acc + (row[cid] || 0), 0);
    }

    return Array.from(map.values());
  }, [partnerSummary, courseIds]);
  const courseTotals = useMemo(() => {
    const totals = {};
    let grandTotal = 0;

    courseIds.forEach((cid) => {
      totals[cid] = partnerSummary.reduce(
        (acc, item) =>
          acc + (String(item.courseId) === String(cid) ? item.count || 0 : 0),
        0
      );
      grandTotal += totals[cid];
    });

    return { ...totals, grandTotal };
  }, [partnerSummary, courseIds]);

  const columns = [
    // {
    //   name: "",
    //   selector: "edit",
    //   cell: (row) => (
    //     <button className="text-blue-500" onClick={() => console.log(row)}>
    //       <FaEdit />
    //     </button>
    //   ),
    // },
    { name: "Learner Id", selector: (row) => row.id },
    {
      name: "Salutation",
      selector: (row) =>
        row?.salutation
          ? row.salutation.charAt(0).toUpperCase() +
            row.salutation.slice(1).toLowerCase()
          : "",
      id: "salutation",
    },
    { name: "Name", selector: (row) => row.name, width: "14rem", wrap: true },
    {
      name: "Email",
      selector: (row) => row.email,
      wrap: true,
      width: "14rem",
    },
    {
      name: "Mobile",
      selector: (row) => row.mobileNumber,
      grow: 4,
      width: "10rem",
    },
    {
      name: "Registered At",
      selector: (row) => moment(row?.createdAt).format("DD MMM Y, h:mm:s a"),
      sortable: true,
      width: "12rem",
      wrap: true,
      id: "createdAt",
    },
    {
      name: "Gender",
      selector: (row) => row.gender,
    },
    {
      name: "Address",
      selector: (row) => row.addressLine1,
      width: "20rem",
      wrap: true,
    },
    {
      name: "Pincode",
      selector: (row) => row.pincode,
    },
    {
      name: "Qualification",
      selector: (row) => QUALIFICATION_NAME[row.highestQualification],
    },
    {
      name: "Identity Proof Number",
      selector: (row) => row.identityProofNumber,
      width: "12rem",
    },
    {
      name: "Partner Name",
      selector: (row) => row.partner,
      grow: 5,
      width: "12rem",
    },
    {
      name: "Batch Name",
      selector: (row) =>
        row?.batchDetails?.map((batch) => batch.batchName).join(", ") || "-",
      wrap: true,
      width: "12rem",
      id: "batchDetails",
    },
    {
      name: "Batch Start Date",
      selector: (row) =>
        row?.batchDetails
          ?.map((batch) => moment(batch?.batchStartDate).format("DD-MMM-YYYY"))
          .join(", ") || "-",
      wrap: true,
      width: "12rem",

      id: "batchDetails",
    },
    {
      name: "Batch End Date",
      selector: (row) =>
        row?.batchDetails
          ?.map((batch) => moment(batch?.batchEndDate).format("DD-MMM-YYYY"))
          .join(", ") || "",
      wrap: true,
      width: "12rem",
      id: "batchDetails",
    },
    {
      name: "Seats Left",
      sortable: true,
      selector: (row) =>
        row?.batchDetails
          ?.map(
            (batch) =>
              `${
                batch?.max_learners -
                batch?.userEnrollmentBatchArray_aggregate?.aggregate.count
              }`
          )
          .join(", ") || "",
      wrap: true,
      id: "batchDetails",
    },
    {
      name: "Language",
      selector: (row) =>
        row?.batchDetails?.map((batch) => batch.batchLanguage).join(", ") ||
        "-",
      grow: 2,
      wrap: true,
      id: "batchDetails",
    },
    {
      name: "Actions",
      cell: (row) => <PartnerUserActionFragment row={row} />,
      grow: 2,
      wrap: true,
      id: "updatedAt",
      width: "65rem",
    },
  ];
  return (
    <>
      <h1 className="mt-10 font-semibold text-lg text-[#2A6EC6]">
        Partner Summary
      </h1>
      {candidatePartnerId ? (
        <div className="grid grid-cols-3 gap-6 mt-4">
          {partnerSummary.map((item) => (
            <div
              key={item.courseId}
              className="p-4 border rounded-lg shadow bg-white"
            >
              <h2 className="font-semibold text-blue-600">
                {courseIdToName.get(String(item.courseId)) ||
                  `Course ${item.courseId}`}
              </h2>
              <p className="mt-2">
                Candidates: <b>{item.candidateCount}</b>
              </p>
              <p>
                Batches: <b>{item.batchCount}</b>
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <div className="border border-gray-300 rounded-lg overflow-x-auto">
            <DataTable
              columns={summaryColumns}
              data={summaryRows}
              pagination
              paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
              paginationPerPage={5}
              fixedHeader
              fixedHeaderScrollHeight="500px"
              customStyles={{
                table: {
                  style: {
                    borderCollapse: "collapse",
                    width: "100%",
                  },
                },
                headRow: {
                  style: {
                    background: "#b1c7e9",
                    color: "#3D3D3D",
                    fontWeight: "600",
                  },
                },
                pagination: {
                  style: {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    borderRadius: "0.375rem",
                    borderTopLeftRadius: "0px",
                    borderTopRightRadius: "0px",
                    border: "1px solid rgba(0,0,0,0.4)",
                    borderTop: "0",
                    fontWeight: "500",
                    color: "rgb(61, 61, 61)",
                  },
                },
              }}
            />

            <div className="overflow-x-auto border border-gray-400">
              <table className="min-w-full border-collapse">
                <tbody>
                  <tr className="bg-gray-100 font-semibold text-center">
                    <td
                      className="p-2 border-r text-left"
                      style={{ width: "10rem" }}
                    >
                      TOTAL
                    </td>

                    {courseIds.map((cid) => (
                      <td
                        key={cid}
                        className="p-5 border-r text-blue-700 font-bold text-left"
                        style={{ width: "10rem" }}
                      >
                        {courseTotals[cid]}
                      </td>
                    ))}

                    <td className="p-4 text-red-600 font-extrabold text-left">
                      {courseTotals.grandTotal}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 mt-2">
        <h1 className="font-semibold text-lg text-[#2A6EC6]">
          Candidates List
        </h1>
        <div className="flex items-center gap-10">
          {!candidatePartnerId && (
            <div className="mt-4">
              <p className="text-xl font-medium">Select Partner</p>
              <select
                className="mt-2 p-2 border rounded"
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
              >
                <option value="">-- Select a Partner --</option>
                {partnersList.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col">
            <div className="flex gap-10">
              <div className="mt-4 w-full">
                <p className="text-xl font-medium">Select Course</p>
                <Select
                  options={[
                    { value: undefined, label: "Select a Course" },
                    ...courses,
                  ]}
                  placeholder="Select Course"
                  value={selectedCourse}
                  onChange={handleCourseChange}
                  getOptionLabel={(e) => e.label || ""}
                  getOptionValue={(e) =>
                    e.value === undefined ? "" : e.value.toString()
                  }
                  className="mt-2 rounded"
                />
              </div>
              <div className="mt-4 w-full">
                <p className="text-xl font-medium">Select Language</p>
                <Select
                  options={[
                    { value: undefined, label: "Select a Language" },
                    ...languages,
                  ]}
                  value={
                    languages.find((lang) => lang.value === selectedLanguage) ||
                    null
                  }
                  onChange={(e) => setSelectedLanguage(e.value)}
                  isDisabled={!selectedCourse?.value}
                  className="w-full mt-2 rounded"
                />
              </div>
            </div>
            <div className="flex gap-20">
              <div className="mt-4">
                <p className="text-xl font-medium">Select Batch</p>
                <Select
                  options={batchOptions}
                  value={selectedBatches}
                  onChange={handleBatchChange}
                  isMulti
                  isClearable
                  className="w-full mt-2 rounded"
                  placeholder="Select Batches"
                />
              </div>

              <h1 className="text-lg font-medium  mt-10 text-[#2A6EC6] border border-gray-600 p-2">
                Candidate Count:
                <span className="ml-2 text-red-500">{totalUsersCount}</span>
              </h1>
            </div>
          </div>
        </div>
        <div className="mt-6 overflow-x-auto">
          <DataTable
            columns={columns}
            data={candidatesList || []}
            pagination
            paginationServer
            paginationRowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
            paginationTotalRows={totalUsersCount}
            paginationPerPage={paginationParams.pageSize}
            paginationDefaultPage={paginationParams.pageIndex + 1}
            onChangePage={(targetPageNumber) =>
              setPaginationParams((prev) => ({
                ...prev,
                pageIndex: targetPageNumber - 1,
              }))
            }
            onChangeRowsPerPage={(newCount) =>
              setPaginationParams({
                pageIndex: 0,
                pageSize: newCount,
              })
            }
            customStyles={{
              table: {
                style: {
                  overflow: "hidden",
                  borderRadius: "8px 8px 0px 0px",
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderLeft: "0",
                },
              },
              headRow: {
                style: {
                  background: "#b1c7e9",
                  color: "#3D3D3D",
                  fontWeight: "600",
                  "&:hover": {
                    cursor: "pointer",
                  },
                },
              },
              headCells: {
                style: {
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderTop: "0",
                  borderRight: "0",
                  borderBottom: "0",
                },
              },
              cells: {
                style: {
                  borderLeft: "1px solid rgba(0,0,0,0.4)",
                },
              },
              rows: {
                style: {
                  backgroundColor: "#fff",
                  cursor: "default",
                  color: "#656363",
                  fontSize: "14px",
                  "&:hover": {
                    cursor: "default",
                    backgroundColor: "#E9E9E9",
                  },
                  borderBottom: "1px solid rgba(0,0,0,0.4)",
                },
              },
              pagination: {
                style: {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "0.375rem",
                  borderTopLeftRadius: "0px",
                  borderTopRightRadius: "0px",
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderTop: "0",
                  fontWeight: "500",
                  color: "rgb(61, 61, 61)",
                },
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

export default PartnerCandidatesList;
